import { BankAccountDetails } from '@40seas/entities/bank-account';
import { CopyIcon } from '@40seas/frontend/common/icons';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { Address } from '@40seas/models';
import { copyToClipboard } from '@40seas/payments-shared';
import { BankConnectivityProvidersService } from '@40seas/shared/bank-connectivity-providers-service';
import { styled, Tooltip } from '@mui/material';
import { Box, Typography, Stack, Button } from '@40seas/frontend/common/components';
import { useNotify } from 'react-admin';

export const BuyerBankDetails = ({
    bankAccountDetails,
    reference,
    externalId,
    isOneOfMany,
}: {
    bankAccountDetails?: BankAccountDetails;
    reference?: string;
    externalId?: string;
    isOneOfMany?: boolean;
}) => {
    const keys = BankConnectivityProvidersService.getVirtualAccountFields(bankAccountDetails?.currency);
    const displayedReference = reference ?? externalId;

    const bankName = bankAccountDetails?.bankName;
    const accountName = bankAccountDetails?.bankAccountName;
    const bankAddress = addressDescription(bankAccountDetails?.bankAddress);

    return (
        <StyledBankDetails>
            <Stack spacing={0.5} mt={2}>
                {!isOneOfMany && <DetailsRow label="Bank Name" value={bankName} />}
                {accountName && <DetailsRow label="Account name" value={accountName} />}
                {keys.map((key) => {
                    if (bankAccountDetails?.bankAccountPayload?.[key]) {
                        return (
                            <Box key={key}>
                                <DetailsRow
                                    label={BankConnectivityProvidersService.formatBankAccountField(key)}
                                    value={bankAccountDetails?.bankAccountPayload?.[key]}
                                />
                            </Box>
                        );
                    }

                    return null;
                })}
                <DetailsRow label="Bank Address" value={bankAddress} />
                {displayedReference && <DetailsRow label="Reference Code" value={displayedReference} />}
            </Stack>
        </StyledBankDetails>
    );
};

const DetailsRow = ({ label, value }: { label: string; value: string | undefined }) => {
    const notify = useNotify();

    const copyOneField = async () => {
        try {
            await copyToClipboard(Promise.resolve(`${value}`));
        } catch (error: any) {
            notify('Failed to copy details to clipboard', { type: 'error' });
            datadogRumProvider.reportToDatadog(error);
            return;
        }
        notify('Copied to clipboard', { type: 'success' });
    };

    if (!value) {
        return null;
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack spacing={0} alignItems="start">
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="body1" textAlign="start">
                    {value}
                </Typography>
            </Stack>
            <Tooltip title={`Copy ${label}`} placement="top">
                <Box>
                    <Button onClick={copyOneField} StartIcon={CopyIcon} variant="text" />
                </Box>
            </Tooltip>
        </Stack>
    );
};

export const StyledBankDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '5px 0 5px 0',
    ['& .BankDetail']: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    ['& .BankDetail p']: {
        textAlign: 'left',
    },
}));

function addressDescription(bankAddress: Address | undefined) {
    return bankAddress
        ? [bankAddress.addressLine1, bankAddress.city, bankAddress.state, bankAddress.zipCode, bankAddress.country]
              .filter(Boolean)
              .join(', ')
        : '';
}
